import type { KeyboardEvent, ReactNode } from 'react';
import SvgChevronRight from 'creative-ui/uikit/svg/arrows/chevron-right';
import { useRef } from 'react';

import './ListBox.css';

export interface ListBoxOption<T extends string> {
  label: ReactNode;
  value: T;
  icon?: ReactNode;
}

export interface ListBoxProps<T extends string> {
  /** The id of the ListBox element. */
  id: string;
  /** The currently selected value. */
  value?: T;
  /** The options to display in the ListBox. */
  options: ListBoxOption<T>[];
  /** Callback when an option is selected. */
  onSelect: (value: T) => void;
  /** Callback when the ListBox is cancelled (e.g. via the Escape key). */
  onCancel?: () => void;
  /** Show a chevron arrow on the right of each item. */
  showArrow?: boolean;
  /** Show a border and shadow around the ListBox. Defaults to `true`. */
  showBorder?: boolean;
  /** Show a separator between items. */
  showSeparator?: boolean;
  className?: string;
}

/**
 * Supports arrow-key roving focus and Escape to cancel.
 */
export function ListBox<T extends string>({
  id,
  value,
  options,
  onSelect,
  onCancel,
  showArrow = false,
  showBorder = true,
  showSeparator = false,
  className,
}: Readonly<ListBoxProps<T>>) {
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const total = options.length;

    if (total === 0) {
      return;
    }

    if (event.key === 'Escape') {
      onCancel?.();

      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();

    const current = itemRefs.current.indexOf(
      document.activeElement as HTMLButtonElement,
    );

    const next =
      event.key === 'ArrowDown'
        ? (current + 1) % total
        : (current <= 0 ? total : current) - 1;

    itemRefs.current[next]?.focus();
  };

  const classNames = [
    'c-listBox',
    showBorder ? 'c-listBox--bordered' : '',
    showSeparator ? 'c-listBox--separated' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classNames}
      id={id}
      role="listbox"
      onKeyDown={handleKeyDown}>
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(element) => {
            itemRefs.current[index] = element;
          }}
          type="button"
          role="option"
          aria-selected={value === option.value}
          className="c-listBox__item"
          onClick={() => {
            onSelect(option.value);
          }}>
          {option.icon}
          <span className="c-listBox__label">{option.label}</span>
          {showArrow && <SvgChevronRight className="c-listBox__arrow" />}
        </button>
      ))}
    </div>
  );
}

export default ListBox;
